import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  Upload,
  Package,
  FileJson,
  Download,
  ShieldAlert,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
} from "lucide-react";
import { scanSBOM, exportVEX, exportSbomPdfUrl } from "../lib/api";
import { severityClass } from "../lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import FeedbackButton from "../components/FeedbackButton";

const VEX_STATES = [
  { value: "in_triage", label: "In triage", tone: "text-slate-700" },
  { value: "exploitable", label: "Exploitable", tone: "text-red-700" },
  { value: "not_affected", label: "Not affected", tone: "text-emerald-700" },
  { value: "false_positive", label: "False positive", tone: "text-slate-600" },
  { value: "resolved", label: "Resolved", tone: "text-emerald-700" },
];

const VEX_JUSTIFICATIONS = [
  { value: "", label: "-" },
  { value: "code_not_present", label: "Code not present" },
  { value: "code_not_reachable", label: "Code not reachable" },
  { value: "requires_configuration", label: "Requires configuration" },
  { value: "requires_dependency", label: "Requires dependency" },
  { value: "requires_environment", label: "Requires environment" },
  { value: "protected_by_compiler", label: "Protected by compiler" },
  { value: "protected_at_runtime", label: "Protected at runtime" },
  { value: "protected_at_perimeter", label: "Protected at perimeter" },
  { value: "protected_by_mitigating_control", label: "Protected by control" },
];

const EXAMPLE_PKG_JSON = JSON.stringify(
  {
    name: "demo-app",
    version: "1.0.0",
    dependencies: {
      lodash: "4.17.15",
      minimist: "1.2.0",
      axios: "0.21.0",
      express: "4.17.1",
    },
  },
  null,
  2
);

export default function SBOMPage() {
  const [content, setContent] = useState("");
  const [projectName, setProjectName] = useState("my-app");
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [decisions, setDecisions] = useState({}); // {vuln_id+purl: {state, justification, detail}}
  const [exporting, setExporting] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) {
      toast.error("File too large (max 1.5 MB)");
      return;
    }
    const text = await f.text();
    setContent(text);
    if (!projectName || projectName === "my-app") {
      setProjectName(f.name.replace(/\.(json|txt|xml)$/i, ""));
    }
  };

  const runScan = async () => {
    if (!content.trim()) {
      toast.error("Paste an SBOM or manifest first");
      return;
    }
    setScanning(true);
    setScan(null);
    setDecisions({});
    try {
      const res = await scanSBOM(content, projectName, null);
      setScan(res);
      const total = res.stats?.total_findings ?? 0;
      const vulnComps = res.stats?.vulnerable_components ?? 0;
      toast.success(`${total} findings across ${vulnComps} components`, {
        description: `Scanned ${res.stats?.components} components via OSV.dev`,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Scan failed";
      toast.error("Scan failed", { description: String(detail) });
    } finally {
      setScanning(false);
    }
  };

  const setDecision = (key, patch) =>
    setDecisions((d) => ({
      ...d,
      [key]: { ...(d[key] || { state: "in_triage" }), ...patch },
    }));

  const exportVex = async () => {
    if (!scan?.findings?.length) return;
    setExporting(true);
    try {
      const list = scan.findings.map((f) => {
        const key = `${f.vuln_id}|${f.component?.purl || ""}`;
        const dec = decisions[key] || {};
        return {
          vuln_id: f.vuln_id,
          cve_id: f.cve_id,
          component_purl: f.component?.purl,
          state: dec.state || "in_triage",
          justification: dec.justification || null,
          detail: dec.detail || null,
          response: dec.state === "exploitable" ? ["update"] : [],
        };
      });
      const doc = await exportVEX(projectName + "-vex", list);
      const blob = new Blob([JSON.stringify(doc, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${projectName || "vex-export"}.cdx.vex.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("VEX exported", {
        description: `${doc.vulnerabilities?.length || 0} statements written`,
      });
    } catch (err) {
      toast.error("VEX export failed", {
        description: err?.message || "Unknown error",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="App">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
          },
        }}
      />
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">SBOM Scanner · VEX Exporter</div>
          <div className="flex-1" />
          <Link
            to="/developers"
            data-testid="sbom-api-link"
            className="hidden md:flex items-center gap-1.5 text-xs font-mono text-violet-700 hover:text-violet-800 border border-violet-200 hover:border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-md px-3 py-1.5 transition-colors"
          >
            Automate via API
          </Link>
          <FeedbackButton variant="header" />
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-12">
        <div className="mb-8">
          <div className="overline mb-2 flex items-center gap-2">
            <Package className="h-3 w-3 text-violet-700" />
            SBOM intake
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Scan dependencies. Triage findings. Export{" "}
            <span className="text-violet-700">CycloneDX VEX</span>.
          </h1>
          <p className="text-slate-600 text-sm md:text-base mt-3 max-w-3xl">
            Paste a CycloneDX/SPDX SBOM, npm <code className="font-mono text-violet-700">package.json</code>, or pip <code className="font-mono text-violet-700">requirements.txt</code>. We match against OSV.dev and produce a triage table you can export as a VEX 1.5 document.
          </p>
        </div>

        <section className="panel p-6 md:p-7 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="overline block mb-2">Project name</label>
              <input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                data-testid="sbom-project-name"
                className="w-full bg-transparent border border-slate-300 rounded-md px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
              />
            </div>
            <div>
              <label className="overline block mb-2">Upload file</label>
              <label className="cursor-pointer flex items-center gap-2 border border-dashed border-slate-300 hover:border-violet-300 rounded-md px-3 py-2 text-sm text-slate-600 hover:text-violet-700 transition-colors">
                <Upload className="h-4 w-4" />
                <span>Choose .json / .txt</span>
                <input
                  type="file"
                  accept=".json,.txt,.cdx,.spdx"
                  className="hidden"
                  onChange={onFile}
                  data-testid="sbom-file-input"
                />
              </label>
            </div>
          </div>

          <label className="overline block mb-2">SBOM / manifest content</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={EXAMPLE_PKG_JSON}
            spellCheck={false}
            data-testid="sbom-content"
            className="w-full h-64 bg-slate-50 border border-slate-200 rounded-md p-3 font-mono text-xs text-slate-800 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200 resize-y"
          />

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={() => setContent(EXAMPLE_PKG_JSON)}
              data-testid="sbom-example-btn"
              className="text-xs font-mono text-slate-600 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-md px-2.5 py-1 transition-colors"
            >
              Load example package.json
            </button>
            <button
              onClick={runScan}
              disabled={scanning}
              data-testid="sbom-scan-btn"
              className="bg-slate-900 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {scanning ? "Scanning OSV.dev…" : "Scan dependencies"}
            </button>
          </div>
        </section>

        {scanning && (
          <section className="panel p-6 relative overflow-hidden fade-up">
            <div className="scanner-line" />
            <div className="overline mb-2">Querying OSV.dev…</div>
            <div className="font-mono text-sm text-violet-700 space-y-1">
              <div>Parsing SBOM</div>
              <div>Batching package queries</div>
              <div>Resolving vulnerability details<span className="caret">&nbsp;</span></div>
            </div>
          </section>
        )}

        {scan && <ScanResults scan={scan} decisions={decisions} setDecision={setDecision} onExport={exportVex} exporting={exporting} />}
      </main>
    </div>
  );
}

function ScanResults({ scan, decisions, setDecision, onExport, exporting }) {
  const stats = scan.stats || {};
  const compsWithVulns = useMemo(
    () => (scan.components || []).filter((c) => c.vuln_count > 0),
    [scan]
  );

  return (
    <>
      <section className="panel p-6 mb-6 fade-up" data-testid="sbom-stats">
        <div className="overline mb-3">Scan result</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Components scanned" value={stats.components} />
          <Stat
            label="Vulnerable components"
            value={stats.vulnerable_components}
            tone={stats.vulnerable_components > 0 ? "text-red-700" : "text-emerald-700"}
          />
          <Stat label="Total findings" value={stats.total_findings} />
          <Stat label="With CVE ID" value={stats.with_cve} />
        </div>
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-500 max-w-xl">
            Triage each finding below. Exports include every finding with its component context.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {scan.scan_id && (stats.total_findings ?? 0) > 0 && (
              <a
                href={exportSbomPdfUrl(scan.scan_id)}
                target="_blank"
                rel="noreferrer"
                data-testid="sbom-pdf-export-btn"
                className="flex items-center gap-2 border border-slate-300 text-slate-800 px-4 py-2 rounded-md text-sm font-medium hover:border-violet-300 hover:text-violet-700 transition-colors"
              >
                <FileText className="h-4 w-4" />
                Export PDF
              </a>
            )}
            <button
              onClick={onExport}
              disabled={exporting || (stats.total_findings ?? 0) === 0}
              data-testid="vex-export-btn"
              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-40"
            >
              <Download className="h-4 w-4" />
              {exporting ? "Building VEX…" : "Export CycloneDX VEX"}
            </button>
          </div>
        </div>
      </section>

      {compsWithVulns.length === 0 ? (
        <section className="panel p-7 text-center" data-testid="sbom-clean">
          <ShieldAlert className="h-6 w-6 text-emerald-700 mx-auto mb-2" />
          <div className="font-display text-lg text-slate-900 mb-1">
            No matching vulnerabilities found
          </div>
          <p className="text-slate-500 text-sm">
            OSV.dev didn't return any advisories for the {stats.components}{" "}
            components you scanned.
          </p>
        </section>
      ) : (
        <div className="space-y-4" data-testid="sbom-findings">
          {compsWithVulns.map((c) => (
            <ComponentFindings
              key={`${c.ecosystem}-${c.name}-${c.version}`}
              comp={c}
              decisions={decisions}
              setDecision={setDecision}
            />
          ))}
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
      <div className="overline">{label}</div>
      <div className={`font-mono font-bold text-2xl mt-1 ${tone}`}>
        {value ?? "-"}
      </div>
    </div>
  );
}

function ComponentFindings({ comp, decisions, setDecision }) {
  return (
    <section className="panel p-5">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <FileJson className="h-4 w-4 text-violet-700" />
        <span className="font-mono text-sm text-slate-900">{comp.name}</span>
        <span className="font-mono text-xs text-slate-500">@ {comp.version}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-sm">
          {comp.ecosystem || "unknown"}
        </span>
        <span className="ml-auto text-xs text-slate-600">
          {comp.vuln_count} finding{comp.vuln_count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="space-y-3">
        {(comp.vulns || []).map((v) => {
          const key = `${v.vuln_id}|${comp.purl || ""}`;
          const dec = decisions[key] || {};
          const state = dec.state || "in_triage";
          const stateTone =
            VEX_STATES.find((s) => s.value === state)?.tone || "text-slate-700";
          const headline =
            (v.summary && v.summary.trim()) ||
            (v.details && v.details.trim().split(/\r?\n/)[0]) ||
            "No description provided by OSV.dev for this advisory.";
          const hasExtra =
            (v.details && v.details.trim() && v.details.trim() !== headline) ||
            (v.references && v.references.length > 0);
          return (
            <div
              key={key}
              className="bg-slate-50 border border-slate-200 rounded-md p-4"
              data-testid={`finding-${v.vuln_id}`}
            >
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {v.cve_id && (
                      <a
                        href={`/cve/${v.cve_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`font-mono text-xs border rounded-sm px-1.5 py-0.5 hover:brightness-125 ${severityClass("HIGH")}`}
                        data-testid={`finding-cve-link-${v.vuln_id}`}
                      >
                        {v.cve_id}
                      </a>
                    )}
                    <a
                      href={`https://osv.dev/vulnerability/${v.vuln_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs text-slate-500 hover:text-violet-700 transition-colors inline-flex items-center gap-1"
                    >
                      {v.vuln_id}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    {v.cvss_vector && (
                      <span className="font-mono text-[10px] text-slate-500 border border-slate-200 rounded-sm px-1.5 py-0.5 max-w-full break-all">
                        {v.cvss_vector}
                      </span>
                    )}
                  </div>
                  <p
                    className="text-sm text-slate-800 leading-relaxed"
                    data-testid={`finding-summary-${v.vuln_id}`}
                  >
                    {headline}
                  </p>
                  {hasExtra && (
                    <FindingDetails v={v} headline={headline} />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="overline block mb-1">VEX state</label>
                  <Select
                    value={state}
                    onValueChange={(val) => setDecision(key, { state: val })}
                  >
                    <SelectTrigger
                      className="bg-slate-50 border-slate-200 text-xs h-9"
                      data-testid={`state-${v.vuln_id}`}
                    >
                      <SelectValue placeholder="In triage" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {VEX_STATES.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          className="text-xs"
                        >
                          <span className={s.tone}>{s.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className={`mt-1 text-[10px] font-mono ${stateTone}`}>
                    → {state}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-1">Justification</label>
                  <Select
                    value={dec.justification || ""}
                    onValueChange={(val) =>
                      setDecision(key, { justification: val || null })
                    }
                  >
                    <SelectTrigger
                      className="bg-slate-50 border-slate-200 text-xs h-9"
                      data-testid={`justification-${v.vuln_id}`}
                    >
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {VEX_JUSTIFICATIONS.map((j) => (
                        <SelectItem
                          key={j.value || "none"}
                          value={j.value || "none"}
                          className="text-xs"
                        >
                          {j.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="overline block mb-1">Detail</label>
                  <input
                    value={dec.detail || ""}
                    onChange={(e) =>
                      setDecision(key, { detail: e.target.value })
                    }
                    placeholder="Optional analyst note"
                    data-testid={`detail-${v.vuln_id}`}
                    className="w-full h-9 bg-slate-50 border border-slate-200 rounded-md px-2 text-xs text-slate-800 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function FindingDetails({ v, headline }) {
  const [open, setOpen] = useState(false);
  const details = (v.details || "").trim();
  const refs = v.references || [];
  const showDetails = details && details !== headline;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs font-mono text-violet-700 hover:text-violet-700 transition-colors"
        data-testid={`finding-expand-${v.vuln_id}`}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {open ? "Hide details" : "Show details"}
        {refs.length > 0 && (
          <span className="text-slate-500">· {refs.length} ref{refs.length === 1 ? "" : "s"}</span>
        )}
      </button>
      {open && (
        <div
          className="mt-2 bg-slate-50 border border-slate-200 rounded-md p-3 space-y-3"
          data-testid={`finding-details-${v.vuln_id}`}
        >
          {showDetails && (
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
              {details}
            </p>
          )}
          {refs.length > 0 && (
            <div>
              <div className="overline mb-1">References</div>
              <ul className="space-y-1">
                {refs.map((r, i) => (
                  <li key={i} className="text-xs">
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-violet-700 hover:text-violet-700 break-all inline-flex items-center gap-1"
                    >
                      {r.type && (
                        <span className="text-[10px] text-slate-500 uppercase mr-1">
                          [{r.type}]
                        </span>
                      )}
                      {r.url}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
